<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {debounce} from "lodash/function";
import axios from "axios";
import {useStore} from "vuex";

import TabContainer from "@/components/global-themed/tabs/TabContainer.vue";
import TabItem from "@/components/global-themed/tabs/TabItem.vue";
import RoundedButton from "@/components/global-themed/buttons/RoundedButton";
import GaugeChart from "@/components/charts/GaugeChart.vue";
import MinorLineChart from "@/components/charts/MinorLineChart.vue";
import AddWebsiteButton from "@/components/website/buttons/themed/AddWebsiteButton.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import InfoMessage from "@/components/website/InfoMessage.vue";
import WebsiteImage from "@/components/website/WebsiteImage.vue";
import SecondWebsiteDiscountBanner from "@/components/website/SecondWebsiteDiscountBanner.vue";
import useUser from "@/composables/useUser";

const store = useStore();
const { hasSeoScoreFeature } = useUser();

const props = defineProps({
  canCreateWebsite: {
    required: true,
    type: Boolean,
  },
  websiteCount: {
    required: true,
    type: Number,
  },
  resellerCount: {
    required: true,
    type: Number,
  },
  childCount: {
    required: true,
    type: Number,
  },
  activeWebsiteId: {
    required: true,
    type: [Number,null],
  },
  resellerFilterCounts: {
    required: true,
    type: Object,
  },
  childFilterCounts: {
    required: true,
    type: Object,
  },
  isPackageUser: {
    required: false,
    default: false,
    type: Boolean,
  },
});

const getChartColour = progress => {
  if (progress > 66) {
    return '#0ACCCC';
  }

  if (progress > 33) {
    return '#AA00FF';
  }

    return '#FF4D88';
}

const loading = ref(true);
const statsLoading = ref(false);
const activeTab = ref('active');
const activeFilter = ref('live');
const order = ref('website');
const orderDirection = ref('asc');
const search = ref('');

const axiosController = new AbortController();

const showPausedFilter = computed(() => {
  if (activeTab.value === 'partners') {
    return props.childFilterCounts.paused > 0;
  }

  return props.resellerFilterCounts.paused > 0;
});

const showToBeDeletedFilter = computed(() => {
  if (activeTab.value === 'partners') {
    return props.childFilterCounts.cancelling > 0;
  }

  return props.resellerFilterCounts.cancelling > 0;
});

const orderBy = async (key) => {
  if(key === order.value) {
    orderDirection.value = orderDirection.value === 'asc' ? 'desc' : 'asc';

    await loadWebsites();
    return;
  }

  order.value = key;
  orderDirection.value = 'asc';

  await loadWebsites();
}

const websites = ref([]);

const loadWebsitesRequest = (params = {}) => {
  return axios.get('/ajax/websites', {
    signal: axiosController.signal,
    params: {
      view: activeTab.value,
      filter: activeFilter.value,
      order: order.value,
      orderDirection: orderDirection.value,
      search: search.value,
      ...params,
    }
  });
}

const loadWebsites = () => {
  loading.value = true;

  loadWebsitesRequest().then(({data}) => {
    websites.value = data.data;
    loading.value = false;
    loadWebsitesWithStats();
  })
}

const loadWebsitesWithStats = () => {
  statsLoading.value = true;
  loadWebsitesRequest({
      incStats: true,
  }).then(({data}) => {
    websites.value = data.data;
    statsLoading.value = false;
  })
}

onMounted(async () => await loadWebsites());
onBeforeUnmount(() => {
  axiosController.abort();
})

const changeTab = (tab) => {
  activeTab.value = tab;
  activeFilter.value = 'live';
};

const goToWebsite = (websiteId, uri) => {
  if (activeFilter.value === 'shared') {
    store.dispatch('agencyMarketplace/impersonate', {websiteId, uri});
    return;
  }

  window.location.href = `/website/select/${websiteId}` + (uri ? `?redirect=${uri}` : '');
}

const reloadWebsites = () => {
  loadWebsites();
}

watch(activeTab, reloadWebsites);
watch(activeFilter, reloadWebsites);
watch(search, debounce(async () => {
  if(search.value !== '' && search.value.length < 3) {
    return;
  }

  await reloadWebsites();
}, 500))
</script>

<template>
  <div class="tw-flex tw-flex-col tw-space-y-6 tw-max-h-website-dashboard tw-overflow-hidden tw-max-w-default-container tw-m-auto">
    <SecondWebsiteDiscountBanner />

    <div class="tw-flex tw-justify-between tw-items-center">
      <div class="tw-flex tw-space-x-8 tw-items-center">
        <h1 class="tw-text-4.5xl tw-text-grey-dark tw-font-semibold tw-font-sans">
          Websites Overview
        </h1>
        <div
          class="tw-rounded-lg tw-text-white tw-bg-primary tw-w-10 tw-h-10 tw-flex tw-justify-center tw-items-center tw-text-xl tw-font-semibold"
          v-text="websiteCount"
        />
      </div>

      <div class="tw-flex tw-items-center tw-gap-3">
        <DefaultButton
          v-if="hasSeoScoreFeature"
          as="a"
          href="/ajax/websites/export-scores"
          target="_blank"
          variant="primary-inverted"
        >
          <svg-vue
            icon="icons/browser"
            class="tw-w-6 tw-h-6 tw-mr-3"
          />
          <span class="tw-font-normal">Export Visibility Scores</span>
        </DefaultButton>
        <AddWebsiteButton v-if="canCreateWebsite" />
      </div>
    </div>

    <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-space-y-8 tw-flex-1 tw-overflow-hidden">
      <TabContainer
        v-if="childCount > 0"
      >
        <TabItem
          :active="activeTab === 'active'"
          class="tw-flex tw-items-center tw-justify-center tw-space-x-3"
          @click="changeTab('active')"
        >
          <svg-vue
            icon="icons/browser-with-plus-icon-in-center"
            class="tw-w-5 tw-h-5"
            :class="{'tw-text-grey': activeTab === 'active'}"
          />
          <span>Added Websites</span>
          <div
            class="tw-rounded tw-text-white tw-bg-primary tw-w-6 tw-h-6 tw-flex tw-justify-center tw-items-center tw-text-xs tw-font-semibold"
            v-text="resellerCount"
          />
        </TabItem>
        <TabItem
          :active="activeTab === 'partners'"
          class="tw-flex tw-items-center tw-justify-center tw-space-x-2"
          @click="changeTab('partners')"
        >
          <svg-vue
            icon="icons/store-with-plus-icon-in-corner"
            class="tw-w-5 tw-h-5"
            :class="{'tw-text-grey': activeTab === 'partners'}"
          />
          <span>Partner Websites</span>
          <div
            class="tw-rounded tw-text-white tw-bg-primary tw-w-6 tw-h-6 tw-flex tw-justify-center tw-items-center tw-text-xs tw-font-semibold"
            v-text="childCount"
          />
        </TabItem>
      </TabContainer>

      <div class="tw-grid tw-grid-cols-2">
        <div class="tw-flex tw-space-x-2">
          <RoundedButton
            :active="activeFilter === 'live'"
            @click="activeFilter = 'live'"
          >
            Live
          </RoundedButton>
          <RoundedButton
            v-if="showPausedFilter"
            :active="activeFilter === 'paused'"
            @click="activeFilter = 'paused'"
          >
            Paused
          </RoundedButton>
          <RoundedButton
            v-if="showToBeDeletedFilter"
            :active="activeFilter === 'toBeDeleted'"
            @click="activeFilter = 'toBeDeleted'"
          >
            To Be Deleted
          </RoundedButton>
          <RoundedButton
            v-if="activeTab !== 'partners' && resellerFilterCounts.shared > 0"
            :active="activeFilter === 'shared'"
            @click="activeFilter = 'shared'"
          >
            Shared
          </RoundedButton>
        </div>

        <div>
          <label class="tw-border-2 tw-border-grey-mid tw-rounded-md tw-flex tw-text-black">
            <svg-vue
              icon="icons/search"
              class="tw-w-7 tw-h-7 tw-my-2 tw-ml-3 tw-text-grey"
            />
            <input
              v-model="search"
              type="search"
              placeholder="Search"
              class="tw-border-0 tw-w-full tw-pl-3 tw-text-lg"
            >
          </label>
        </div>
      </div>

      <div class="-tw-mx-8 tw-border-y tw-border-grey-mid">
        <div class="tw-grid tw-grid-cols-12 tw-px-12 tw-divide-x tw-divide-grey-mid tw-h-12">
          <div
            class="tw-py-2 tw-flex tw-items-center tw-cursor-pointer tw-group tw-text-grey-dark"
            :class="{
              'tw-col-span-7': !isPackageUser,
              'tw-col-span-9': isPackageUser,
            }"
            @click="orderBy('website')"
          >
            Website
            <svg-vue
              icon="icons/chevron"
              class="tw-w-2 tw-h-2 tw-ml-3 tw-transition-transform duration-500"
              :class="{
                'tw-rotate-180': order === 'website' && orderDirection === 'desc',
                'tw-opacity-0 group-hover:tw-opacity-100 group-hover:tw-text-primary group-hover:tw-animate-pulse': order !== 'website',
              }"
            />
          </div>
          <div
            v-if="!isPackageUser"
            class="tw-col-span-2 tw-pl-2 tw-py-2 tw-text-center tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-group tw-text-grey-dark"
            @click="orderBy('action')"
          >
            Action Progress
            <svg-vue
              icon="icons/chevron"
              class="tw-w-2 tw-h-2 tw-ml-3 tw-transition-transform duration-500"
              :class="{
                'tw-rotate-180': order === 'action' && orderDirection === 'desc',
                'tw-opacity-0 group-hover:tw-opacity-100 group-hover:tw-text-primary group-hover:tw-animate-pulse': order !== 'action',
              }"
            />
          </div>
          <div
            class="tw-col-span-2 tw-pl-2 tw-py-2 tw-text-center tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-group tw-text-grey-dark"
            @click="orderBy('keywords')"
          >
            Average Keyword Rankings
            <svg-vue
              icon="icons/chevron"
              class="tw-w-2 tw-h-2 tw-ml-3 tw-transition-transform duration-500"
              :class="{
                'tw-rotate-180': order === 'keywords' && orderDirection === 'desc',
                'tw-opacity-0 group-hover:tw-opacity-100 group-hover:tw-text-primary group-hover:tw-animate-pulse': order !== 'keywords',
              }"
            />
          </div>
          <div class="tw-col-span-1 tw-p-2 tw-text-center tw-flex tw-items-center tw-justify-center tw-text-grey-dark">
            Switch to
          </div>
        </div>
      </div>

      <div class="tw-grid tw-grid-cols-1 tw-gap-4 !tw-mt-4 -tw-mx-8 tw-overflow-y-scroll">
        <template v-if="loading">
          <div class="tw-flex tw-flex-col tw-items-center tw-space-y-10 tw-justify-center tw-my-24 tw-w-full tw-max-w-sm tw-mx-auto">
            <svg-vue
              icon="icons/loading"
              class="tw-w-1/2 tw-h-auto tw-text-primary/20"
            />
            <p class="tw-text-2xl tw-text-center">
              Loading
            </p>
          </div>
        </template>
        <template v-else-if="websites.length > 0">
          <div
            v-for="website in websites"
            :key="website.id"
            class="tw-grid tw-grid-cols-12 tw-rounded-xl tw-border tw-mx-8 tw-h-40 tw-overflow-hidden"
            :class="[
              activeWebsiteId === website.originalId ? 'tw-border-primary' : 'tw-border-grey-light'
            ]"
          >
            <div
              class="tw-flex tw-w-full tw-space-x-4"
              :class="{
                'tw-col-span-7': !isPackageUser,
                'tw-col-span-9': isPackageUser,
              }"
            >
              <WebsiteImage
                :src="website.image"
                @click="goToWebsite(website.originalId)"
              />
              <div class="tw-flex-1 tw-overflow-hidden tw-my-auto">
                <div class="tw-flex">
                  <div
                    v-tooltip="{
                      content: 'View Dashboard',
                      placement: 'bottom',
                      theme: 'dark-blue-tooltip',
                    }"
                    class="tw-overflow-hidden tw-text-ellipsis tw-text-primary tw-underline hover:tw-text-black tw-transition-all tw-text-xl tw-flex tw-items-center tw-space-x-3 tw-pr-2"
                    @click="goToWebsite(website.originalId)"
                  >
                    <span>{{ website.shortUrl }}</span>
                  </div>

                  <a
                    v-tooltip="{
                      content: 'View Website',
                      placement: 'bottom',
                      theme: 'dark-blue-tooltip',
                    }"
                    :href="website.url"
                    target="_blank"
                    class="tw-overflow-hidden tw-text-ellipsis tw-text-primary tw-underline hover:tw-text-black tw-transition-all tw-text-xl tw-flex tw-items-center tw-space-x-3 tw-pr-2"
                  >
                    <svg-vue
                      icon="icons/external-thin"
                      class="tw-w-4 tw-h-4 tw-flex-shrink-0"
                    />
                  </a>
                  <div
                    v-tooltip="{
                      content: 'Settings',
                      placement: 'bottom',
                      theme: 'dark-blue-tooltip',
                    }"
                    class="tw-overflow-hidden tw-text-ellipsis tw-text-primary tw-underline hover:tw-text-black tw-transition-all tw-text-xl tw-flex tw-items-center tw-space-x-3 tw-pr-2 tw-cursor-pointer"
                    @click="goToWebsite(website.originalId, `/website/settings/${website.id}`)"
                  >
                    <svg-vue
                      icon="icons/settings"
                      class="tw-w-4 tw-h-4 tw-flex-shrink-0"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!isPackageUser"
              class="tw-px-4 tw-col-span-2 tw-relative tw-h-30 tw-cursor-pointer align-self-center"
              :class="{
                'hover:tw-bg-primary-300 tw-rounded-lg': website.actions && !website.isKitWebsite
              }"
              @click="goToWebsite(website.originalId,'/actions')"
            >
              <!-- Spacefiller if website has kit -->
              <div v-if="website.isKitWebsite" />

              <div
                v-else-if="statsLoading"
                class="tw-flex tw-h-full"
              >
                <svg-vue
                  icon="icons/loading"
                  class="tw-w-1/2 tw-h-auto tw-text-primary/20 m-auto"
                />
              </div>
              <template v-else-if="website.actions">
                <GaugeChart
                  :value="website.actions.percentage"
                  :color="getChartColour(website.actions.percentage)"
                />
                <span class="tw-absolute tw-bottom-3 tw-w-full tw-left-0 tw-text-center tw-text-sm">
                  Resets in {{ website.actions.resets }} days
                </span>
              </template>
              <div
                v-else
                class="tw-flex tw-items-center tw-space-x-2 tw-px-4 tw-col-span-2 tw-relative tw-h-30"
              >
                <InfoMessage>Upgrade your account to view</InfoMessage>
              </div>
            </div>

            <div
              class="tw-px-4 tw-col-span-2 tw-my-auto tw-cursor-pointer"
              :class="{
                'hover:tw-bg-primary-300 tw-rounded-lg' : website.keywordRankings !== null && website.keywordRankings.length > 0
              }"
              @click="goToWebsite(website.originalId,'/keywords')"
            >
              <div
                v-if="statsLoading"
                class="tw-flex tw-h-full"
              >
                <svg-vue
                  icon="icons/loading"
                  class="tw-w-1/2 tw-h-auto tw-text-primary/20 m-auto"
                />
              </div>
              <div
                v-else-if="website.keywordRankings === null"
                class="tw-flex tw-items-center tw-space-x-2 tw-px-4 tw-col-span-2 tw-relative tw-h-30"
              >
                <InfoMessage>Upgrade your account to view</InfoMessage>
              </div>
              <div
                v-else-if="website.keywordRankings?.length === 0"
                class="tw-flex tw-items-center tw-space-x-2 tw-px-4 tw-col-span-2 tw-relative tw-h-30"
              >
                <InfoMessage>Sorry, no rankings found.</InfoMessage>
              </div>
              <template v-else>
                <MinorLineChart
                  v-if="website.keywordRankings"
                  :data="website.keywordRankings"
                  with-background-gradient
                />
              </template>
            </div>

            <div class="tw-px-4 tw-col-span-1 tw-my-auto tw-mx-auto">
              <div
                class=" tw-border-2 tw-border-primary tw-rounded-lg tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-transition-all tw-cursor-pointer hover:tw-shadow-blue"
                :class="[
                  activeWebsiteId === website.originalId ? 'tw-text-white tw-bg-primary' : 'tw-text-primary tw-bg-white'
                ]"
                @click="goToWebsite(website.originalId)"
              >
                <svg-vue
                  icon="icons/switch"
                  class="tw-w-6 tw-h-6 tw-flex-shrink-0"
                />
              </div>
            </div>
          </div>
        </template>

        <div
          v-else
          class="tw-flex tw-flex-col tw-items-center tw-space-y-10 tw-justify-center tw-my-24 tw-w-full tw-max-w-sm tw-mx-auto"
        >
          <svg-vue
            icon="illustrations/landscape-blue"
            class="tw-w-4/5"
          />

          <p class="tw-text-2xl tw-text-center">
            Looks like there are no websites here. Add one now!
          </p>

          <AddWebsiteButton
            v-if="canCreateWebsite"
            class="!tw-mt-14"
          />
        </div>
      </div>
    </div>
  </div>
</template>
